import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState(searchParams.get("expired") === "1"
    ? "Session expirée pour inactivité. Reconnecte-toi pour continuer."
    : "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Veuillez remplir les deux champs.");
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" style={{ background: "var(--bg)" }}>
      <div
        className="hidden md:flex relative bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1545471977-94cac22e71ed?fm=jpg&q=80&w=1600&auto=format&fit=crop')",
          filter: "grayscale(100%) contrast(1.08)",
        }}
      >
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 p-12 flex flex-col justify-between text-white w-full">
          <div className="flex items-center gap-3">
            <Logo size={48} invert />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest opacity-70 mb-3">
              Plateforme de gestion
            </div>
            <h1 className="font-serif text-5xl lg:text-7xl leading-[0.95]">
              Tes mandats,
              <br />
              <em>orchestrés.</em>
            </h1>
            <p className="font-mono text-xs mt-6 max-w-md opacity-80 leading-relaxed">
              Génère tes horaires, assigne tes officiels, détecte les conflits en temps réel, facture tes clients.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md" data-testid="login-card">
          <div className="md:hidden mb-8">
            <Logo size={40} />
          </div>
          <div className="label mb-2">Accès gestionnaire</div>
          <h2 className="h2-mid mb-2">Connexion.</h2>
          <p className="text-sm text-[var(--muted)] mb-8 leading-relaxed">
            Entre tes identifiants pour accéder à la gestion.
          </p>

          <form onSubmit={submit} className="space-y-6">
            {info && (
              <div
                className="border border-black p-3 text-sm leading-snug"
                style={{ background: "var(--surface-3)" }}
                data-testid="login-session-expired-banner"
              >
                <span className="font-bold">⏱ </span>
                {info}
              </div>
            )}
            <div>
              <label className="label block mb-2">Courriel</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                autoComplete="username"
                autoFocus
                className="input-line"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="label block mb-2">Mot de passe</label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="current-password"
                  className="input-line pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-0 top-2 text-[var(--muted)] hover:text-black"
                  data-testid="toggle-password-btn"
                >
                  {show ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="border-2 border-black p-3 text-xs bg-conflict-stripes-light font-mono" data-testid="login-error">
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} className="btn-primary w-full justify-center" data-testid="login-submit-btn">
              <Lock size={14} strokeWidth={2.5} />
              {loading ? "Vérification…" : "Se connecter"}
            </button>
          </form>

          <div className="mt-10 pt-6 border-t border-[var(--border)] font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] flex flex-wrap items-center justify-between gap-2">
            <span>Verrouillage après 5 tentatives échouées · 15 min</span>
            <a href="/confidentialite" className="underline hover:text-black" data-testid="login-privacy-link">
              Confidentialité
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
